// Visual Flow Builder Client Layer (PRD §1.9, WP-08).
// Data models, 17 node definitions, callables, and catalogue helpers.
import { call } from './callable';

export const FLOW_NODE_TYPES = [
  'text',
  'image',
  'audio',
  'video',
  'file',
  'location',
  'ai_reply',
  'user_input_flow',
  'interactive_message',
  'template_message',
  'cta_url_button',
  'condition',
  'subscribe_sequence',
  'label_assign',
  'random_number',
  'http_api',
  'google_sheet_fetch',
] as const;

export type FlowNodeType = (typeof FLOW_NODE_TYPES)[number];

export const FLOW_TYPES = ['keyword', 'system', 'sequence'] as const;
export type FlowType = (typeof FLOW_TYPES)[number];

export interface FlowTrigger {
  keywords?: string[];
  matchType?: 'exact' | 'contains';
  systemActionKey?: string | null;
  sequenceConfig?: {
    deliveryTime: string;
    timezone: string;
    reentry: 'never' | 'after_completion' | 'always';
  } | null;
}

export interface AutomationActions {
  addLabelIds?: string[];
  removeLabelIds?: string[];
  addListIds?: string[];
  removeListIds?: string[];
  subscribeSequenceId?: string | null;
  unsubscribeSequenceId?: string | null;
  assignRoleId?: string | null;
  assignUid?: string | null;
}

export interface DataIntegrations {
  saveToCustomField?: { key: string; variable: string } | null;
  forwardWebhookId?: string | null;
  syncSheetFetchId?: string | null;
}

export interface FlowEdge {
  id: string;
  source: string;
  sourceHandle?: string | null;
  target: string;
  targetHandle?: string | null;
}

export interface FlowNode<T = any> {
  id: string;
  type: FlowNodeType;
  data: T;
  position?: { x: number; y: number };
}

export interface FlowDoc {
  id: string;
  tenantId: string;
  name: string;
  type: FlowType;
  integrationId?: string | null;
  folderId?: string | null;
  trigger?: FlowTrigger;
  automationActions?: AutomationActions;
  dataIntegrations?: DataIntegrations;
  nodes: FlowNode[];
  edges: FlowEdge[];
  published: boolean;
  version: number;
  draftOf?: string | null;
  createdAt: number;
  updatedAt: number;
}

// ── Per-Node Data Shapes ──────────────────────────────────────────────────────

export interface TextNodeData {
  text: string;
}

export interface MediaNodeData {
  mediaUrl?: string;
  mediaId?: string;
  caption?: string;
  fileName?: string;
}

export interface LocationNodeData {
  latitude: number;
  longitude: number;
  name?: string;
  address?: string;
}

export interface AiReplyNodeData {
  systemPrompt?: string;
  agentId?: string;
  temperature?: number;
}

export interface UserInputFlowNodeData {
  prompt: string;
  variableName: string;
  validationType?: 'text' | 'number' | 'email' | 'phone';
  retryMessage?: string;
}

export interface InteractiveButton {
  id: string;
  title: string;
}

export interface InteractiveMessageNodeData {
  header?: { type: 'text'; text: string };
  body: string;
  footer?: string;
  buttons: InteractiveButton[];
}

export interface TemplateMessageNodeData {
  templateName: string;
  templateLanguage?: string;
  variableMappings?: Record<string, string>;
}

export interface CtaUrlButtonNodeData {
  body: string;
  footer?: string;
  buttonText: string;
  url: string;
}

export type ConditionOperator =
  | 'equals'
  | 'not_equals'
  | 'contains'
  | 'greater_than'
  | 'less_than'
  | 'in_list'
  | 'has_label'
  | 'time_between';

export interface ConditionRule {
  field: string;
  operator: ConditionOperator;
  value: any;
}

export interface ConditionNodeData {
  rules: ConditionRule[];
  logicalOperator: 'and' | 'or';
}

export interface SubscribeSequenceNodeData {
  sequenceId: string;
}

export interface LabelAssignNodeData {
  addLabelIds?: string[];
  removeLabelIds?: string[];
}

export interface RandomNumberNodeData {
  min: number;
  max: number;
  variableName: string;
}

export interface HttpApiNodeData {
  apiId: string;
  customPayload?: Record<string, any>;
}

export interface GoogleSheetFetchNodeData {
  fetchId: string;
  keyVariable: string;
}

// ── Node Catalogue & Factory ──────────────────────────────────────────────────

export type NodeCategory = 'messages' | 'data' | 'interactive' | 'control' | 'integrations';

export interface NodeCatalogueItem {
  type: FlowNodeType;
  category: NodeCategory;
  labelKey: string;
  defaultLabel: string;
  descKey: string;
  defaultDesc: string;
  icon: string;
  badge?: string;
  colorClass: string;
}

export const NODE_CATALOGUE: NodeCatalogueItem[] = [
  // 1. Messages
  {
    type: 'text',
    category: 'messages',
    labelKey: 'flow.nodes.text',
    defaultLabel: 'Text Message',
    descKey: 'flow.nodes.textDesc',
    defaultDesc: 'Send a regular WhatsApp text message with template variables.',
    icon: '💬',
    colorClass: 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  },
  {
    type: 'image',
    category: 'messages',
    labelKey: 'flow.nodes.image',
    defaultLabel: 'Image',
    descKey: 'flow.nodes.imageDesc',
    defaultDesc: 'Send a photo or graphic image with an optional caption.',
    icon: '🖼️',
    colorClass: 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  },
  {
    type: 'audio',
    category: 'messages',
    labelKey: 'flow.nodes.audio',
    defaultLabel: 'Voice / Audio',
    descKey: 'flow.nodes.audioDesc',
    defaultDesc: 'Send a WhatsApp voice note or audio recording.',
    icon: '🎤',
    colorClass: 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  },
  {
    type: 'video',
    category: 'messages',
    labelKey: 'flow.nodes.video',
    defaultLabel: 'Video',
    descKey: 'flow.nodes.videoDesc',
    defaultDesc: 'Send an MP4 video clip with an optional caption.',
    icon: '🎬',
    colorClass: 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  },
  {
    type: 'file',
    category: 'messages',
    labelKey: 'flow.nodes.file',
    defaultLabel: 'Document File',
    descKey: 'flow.nodes.fileDesc',
    defaultDesc: 'Send a PDF, Word doc, or file attachment.',
    icon: '📄',
    colorClass: 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  },
  {
    type: 'location',
    category: 'messages',
    labelKey: 'flow.nodes.location',
    defaultLabel: 'Location Pin',
    descKey: 'flow.nodes.locationDesc',
    defaultDesc: 'Send geographical map coordinates and address.',
    icon: '📍',
    colorClass: 'border-blue-500 bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
  },
  {
    type: 'ai_reply',
    category: 'messages',
    labelKey: 'flow.nodes.aiReply',
    defaultLabel: 'AI Response',
    descKey: 'flow.nodes.aiReplyDesc',
    defaultDesc: 'Generate contextual reply with AI knowledge agent.',
    icon: '✨',
    colorClass: 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300',
  },

  // 2. Data Collection
  {
    type: 'user_input_flow',
    category: 'data',
    labelKey: 'flow.nodes.inputFlow',
    defaultLabel: 'User Input Question',
    descKey: 'flow.nodes.inputFlowDesc',
    defaultDesc: 'Prompt customer for an answer, validate input, and save to variable.',
    icon: '❓',
    colorClass: 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300',
  },

  // 3. Interactive
  {
    type: 'interactive_message',
    category: 'interactive',
    labelKey: 'flow.nodes.interactive',
    defaultLabel: 'Interactive Buttons',
    descKey: 'flow.nodes.interactiveDesc',
    defaultDesc: 'Send quick-reply buttons (up to 3) routing to separate branches.',
    icon: '🔘',
    colorClass: 'border-purple-500 bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300',
  },
  {
    type: 'template_message',
    category: 'interactive',
    labelKey: 'flow.nodes.template',
    defaultLabel: 'Template Message',
    descKey: 'flow.nodes.templateDesc',
    defaultDesc: 'Approved Meta template allowed outside the 24h messaging window.',
    icon: '🧩',
    badge: '24h Safe',
    colorClass: 'border-purple-500 bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300',
  },
  {
    type: 'cta_url_button',
    category: 'interactive',
    labelKey: 'flow.nodes.ctaUrl',
    defaultLabel: 'CTA Web Link Button',
    descKey: 'flow.nodes.ctaUrlDesc',
    defaultDesc: 'Interactive message with a direct clickable web link button.',
    icon: '🔗',
    colorClass: 'border-purple-500 bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300',
  },

  // 4. Flow Control
  {
    type: 'condition',
    category: 'control',
    labelKey: 'flow.nodes.condition',
    defaultLabel: 'Condition Branch',
    descKey: 'flow.nodes.conditionDesc',
    defaultDesc: 'Branch based on contact labels, custom fields, time of day, or variables.',
    icon: '🔀',
    badge: 'True / False',
    colorClass: 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  },
  {
    type: 'subscribe_sequence',
    category: 'control',
    labelKey: 'flow.nodes.sequence',
    defaultLabel: 'Enroll in Sequence',
    descKey: 'flow.nodes.sequenceDesc',
    defaultDesc: 'Enroll contact into a multi-day automated drip sequence campaign.',
    icon: '⏩',
    colorClass: 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  },
  {
    type: 'label_assign',
    category: 'control',
    labelKey: 'flow.nodes.labels',
    defaultLabel: 'Assign / Remove Labels',
    descKey: 'flow.nodes.labelsDesc',
    defaultDesc: 'Tag or untag contacts with CRM labels based on user journey.',
    icon: '🏷️',
    colorClass: 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  },
  {
    type: 'random_number',
    category: 'control',
    labelKey: 'flow.nodes.randomNumber',
    defaultLabel: 'Random Generator',
    descKey: 'flow.nodes.randomNumberDesc',
    defaultDesc: 'Generate random number for A/B testing splits or lottery giveaways.',
    icon: '🎲',
    colorClass: 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300',
  },

  // 5. Integrations
  {
    type: 'http_api',
    category: 'integrations',
    labelKey: 'flow.nodes.httpApi',
    defaultLabel: 'HTTP Webhook API',
    descKey: 'flow.nodes.httpApiDesc',
    defaultDesc: 'Execute outbound REST request and capture JSON response into variables.',
    icon: '📡',
    badge: 'API',
    colorClass: 'border-cyan-500 bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300',
  },
  {
    type: 'google_sheet_fetch',
    category: 'integrations',
    labelKey: 'flow.nodes.sheetFetch',
    defaultLabel: 'Google Sheet Row Fetch',
    descKey: 'flow.nodes.sheetFetchDesc',
    defaultDesc: 'Look up live spreadsheet row and map columns into flow variables.',
    icon: '📊',
    badge: 'Sheets',
    colorClass: 'border-cyan-500 bg-cyan-50 text-cyan-700 dark:bg-cyan-950/40 dark:text-cyan-300',
  },
];

export function createDefaultNodeData(type: FlowNodeType): any {
  switch (type) {
    case 'text':
      return { text: 'Hello! Welcome to our automated service.' } as TextNodeData;
    case 'image':
      return { caption: 'Here is your requested image.' } as MediaNodeData;
    case 'audio':
      return {} as MediaNodeData;
    case 'video':
      return { caption: 'Here is your video.' } as MediaNodeData;
    case 'file':
      return { fileName: 'document.pdf', caption: 'Your requested document.' } as MediaNodeData;
    case 'location':
      return {
        latitude: 23.588,
        longitude: 58.3829,
        name: 'Our Main Office',
        address: 'Muscat, Oman',
      } as LocationNodeData;
    case 'ai_reply':
      return {
        systemPrompt: 'You are a helpful customer support agent.',
        temperature: 0.7,
      } as AiReplyNodeData;
    case 'user_input_flow':
      return {
        prompt: 'Please enter your email address:',
        variableName: 'customer_email',
        validationType: 'email',
        retryMessage: 'Invalid email address. Please enter a valid email:',
      } as UserInputFlowNodeData;
    case 'interactive_message':
      return {
        body: 'How can we help you today?',
        buttons: [
          { id: 'btn_support', title: 'Customer Support' },
          { id: 'btn_sales', title: 'Sales Inquiry' },
        ],
      } as InteractiveMessageNodeData;
    case 'template_message':
      return {
        templateName: '',
        templateLanguage: 'en',
        variableMappings: {},
      } as TemplateMessageNodeData;
    case 'cta_url_button':
      return {
        body: 'Visit our website for special seasonal offers!',
        buttonText: 'Visit Website',
        url: 'https://example.com',
      } as CtaUrlButtonNodeData;
    case 'condition':
      return {
        rules: [{ field: 'variables.customer_email', operator: 'not_equals', value: '' }],
        logicalOperator: 'and',
      } as ConditionNodeData;
    case 'subscribe_sequence':
      return { sequenceId: '' } as SubscribeSequenceNodeData;
    case 'label_assign':
      return { addLabelIds: [], removeLabelIds: [] } as LabelAssignNodeData;
    case 'random_number':
      return { min: 1, max: 100, variableName: 'random_split' } as RandomNumberNodeData;
    case 'http_api':
      return { apiId: '', customPayload: {} } as HttpApiNodeData;
    case 'google_sheet_fetch':
      return { fetchId: '', keyVariable: 'phone' } as GoogleSheetFetchNodeData;
    default:
      return {};
  }
}

// ── Callables ────────────────────────────────────────────────────────────────

export interface FlowSavePayload {
  id?: string;
  name: string;
  type?: FlowType;
  integrationId?: string | null;
  folderId?: string | null;
  trigger?: FlowTrigger;
  automationActions?: AutomationActions;
  dataIntegrations?: DataIntegrations;
  nodes: FlowNode[];
  edges: FlowEdge[];
}

export const flowSave = call<
  FlowSavePayload,
  { ok: boolean; id: string; version: number }
>('flowSave');

export const flowPublish = call<
  { id: string },
  { ok: boolean; version: number }
>('flowPublish');

export const flowList = call<
  { type?: FlowType; integrationId?: string; folderId?: string },
  { ok: boolean; flows: FlowDoc[] }
>('flowList');

export const flowGet = call<
  { id: string },
  { ok: boolean; flow: FlowDoc }
>('flowGet');

export const flowDelete = call<
  { id: string },
  { ok: boolean }
>('flowDelete');

export const flowDuplicate = call<
  { id: string },
  { ok: boolean; flow: FlowDoc }
>('flowDuplicate');

export const flowExport = call<
  { id: string },
  { ok: boolean; exportConfig: object }
>('flowExport');

export const flowImport = call<
  {
    config: {
      name: string;
      type: FlowType;
      trigger?: any;
      automationActions?: any;
      dataIntegrations?: any;
      nodes: FlowNode[];
      edges: FlowEdge[];
    };
  },
  { ok: boolean; id: string; flow: FlowDoc }
>('flowImport');

export const flowValidate = call<
  { nodes: FlowNode[]; edges: FlowEdge[] },
  { ok: boolean; valid: boolean; errors: string[]; warnings: string[] }
>('flowValidate');

export const migrateAutomationsToFlows = call<
  Record<string, never>,
  { ok: boolean; migratedCount: number; flows: FlowDoc[] }
>('migrateAutomationsToFlows');

export interface FlowValidationIssue {
  level: 'error' | 'warning';
  message: string;
  nodeId?: string;
}

export function validateFlowGraph(
  name: string,
  type: FlowType,
  nodes: any[],
  edges: any[],
  trigger?: FlowTrigger
): FlowValidationIssue[] {
  const issues: FlowValidationIssue[] = [];
  if (!name.trim()) {
    issues.push({ level: 'error', message: 'Flow name cannot be empty' });
  }
  if (!nodes || nodes.length === 0) {
    issues.push({ level: 'error', message: 'Flow must have at least one node' });
    return issues;
  }
  if (type === 'keyword' && (!trigger?.keywords || trigger.keywords.length === 0)) {
    issues.push({ level: 'warning', message: 'No trigger keywords defined for keyword flow' });
  }
  if (type === 'system' && !trigger?.systemActionKey) {
    issues.push({ level: 'warning', message: 'No target system action selected' });
  }

  if (nodes.length > 1) {
    const connectedNodeIds = new Set<string>();
    for (const e of edges) {
      connectedNodeIds.add(e.source);
      connectedNodeIds.add(e.target);
    }
    for (const n of nodes) {
      if (!connectedNodeIds.has(n.id)) {
        issues.push({
          level: 'warning',
          message: `Disconnected block "${n.id}"`,
          nodeId: n.id,
        });
      }
    }
  }

  return issues;
}

export function exportFlowJson(flow: {
  name: string;
  type: FlowType;
  integrationId?: string | null;
  folderId?: string | null;
  trigger?: FlowTrigger;
  automationActions?: AutomationActions;
  dataIntegrations?: DataIntegrations;
  nodes: FlowNode[];
  edges: FlowEdge[];
}) {
  const jsonStr = JSON.stringify(flow, null, 2);
  const blob = new Blob([jsonStr], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${flow.name.replace(/\s+/g, '_').toLowerCase() || 'flow'}_export.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

